import {
  Add,
  Close,
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AnyEntity,
  EntityType,
  Location,
  Relationship,
} from '@/lib/types';

import AuthGuard from '@/components/auth-guard';
import { ExistingRelationshipCard } from '@/components/existing-relationship-card';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { Page } from '@/components/page';
import { PartOfRelationshipManager } from '@/components/part-of-relationship-manager';
import { RelationshipEntityHero } from '@/components/relationship-entity-hero';
import {
  RelationshipEntityTypeFilter,
  type RelationshipEntityTypeFilterOption,
} from '@/components/relationship-entity-type-filter';
import {
  isOrganizationHierarchyManagedRelationship,
  ORGANIZATION_HIERARCHY_FEATURE_KEY,
} from '@/features/organizations/utils/organization-hierarchy-feature';
import { useEntities, useAllRelationships } from '@/hooks/use-entities';
import { useEntity } from '@/hooks/use-entity';
import { useRelationshipEntityTypeFilter } from '@/hooks/use-relationship-entity-type-filter';
import { useRelationships } from '@/hooks/use-relationships';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';

type TargetTypeOption = {
  value: string;
  entityType: EntityType;
  label: string;
  itemLabel: string;
  codexCategory?: string;
};

type BuiltInTargetTypeOption = {
  value: EntityType;
  entityType: EntityType;
};

type RelationshipViewMode = 'list' | 'graph';

const getRelationshipViewMode = (view: unknown): RelationshipViewMode => (
  view === 'graph' ? 'graph' : 'list'
);

const builtInTargetTypes: BuiltInTargetTypeOption[] = [
  { value: 'characters', entityType: 'characters' },
  { value: 'locations', entityType: 'locations' },
  { value: 'organizations', entityType: 'organizations' },
  { value: 'events', entityType: 'events' },
];

const genericRelationshipSuggestions = [
  'relationships.suggestions.allied_with',
  'relationships.suggestions.enemy_of',
  'relationships.suggestions.parent_of',
  'relationships.suggestions.child_of',
  'relationships.suggestions.married_to',
  'relationships.suggestions.sibling_of',
  'relationships.suggestions.works_for',
  'relationships.suggestions.located_in',
  'relationships.suggestions.controls',
  'relationships.suggestions.founded',
  'relationships.suggestions.member_of',
  'relationships.suggestions.rival_of',
  'relationships.suggestions.caused',
  'relationships.suggestions.was_caused_by',
  'relationships.suggestions.neighbor_of',
  'relationships.suggestions.part_of',
  'relationships.suggestions.protects',
  'relationships.suggestions.hates',
  'relationships.suggestions.loves',
];

function capitalizeFirstLetter(string: string) {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function getCodexCategory(entity: { entityType: string }) {
  if (entity.entityType !== 'codex' || !('type' in entity)) return undefined;
  return typeof entity.type === 'string' && entity.type ? entity.type : undefined;
}

function getEntityTypeFilterId(entity: { entityType: string }) {
  const codexCategory = getCodexCategory(entity);
  return codexCategory ? `codex:${codexCategory}` : entity.entityType;
}

function ExistingRelationships({
  entity,
  novelId,
  returnView,
}: {
  entity: AnyEntity;
  novelId: string;
  returnView: RelationshipViewMode;
}) {
  const { relationships, isLoading, error } = useRelationships(novelId, entity.id);
  const { toast } = useToast();
  const { t } = useTranslation();
  const { hasFeature } = useSubscription();
  const isOrganizationHierarchyEnabled = hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY);
  const { novel, saveItem, deleteItem } = useNovelRepository();
  const {
    hiddenEntityTypeIds,
    showAllEntityTypes,
    toggleEntityType,
  } = useRelationshipEntityTypeFilter(novelId);
  const [editingRelId, setEditingRelId] = useState<string | null>(null);
  const [editingForwardDesc, setEditingForwardDesc] = useState('');
  const [editingReverseDesc, setEditingReverseDesc] = useState('');
  const [deleteRelId, setDeleteRelId] = useState<string | null>(null);


  const handleUpdateRelationship = async (relationshipId: string, rawRel: Relationship) => {
    try {
      const updated: Relationship = {
        ...rawRel,
        id: relationshipId,
        forwardDescription: editingForwardDesc,
      };
      const trimmedReverseDescription = editingReverseDesc.trim();
      if (trimmedReverseDescription) {
        updated.reverseDescription = trimmedReverseDescription;
      } else {
        delete updated.reverseDescription;
      }
      await saveItem('entities', 'relationships', updated);
      toast({ title: t('relationships.relationship_updated') });
      setEditingRelId(null);
      setEditingForwardDesc('');
      setEditingReverseDesc('');
    } catch {
      toast({
        title: t('relationships.relationship_update_error_title'),
        description: t('relationships.relationship_update_error_desc'),
        variant: "destructive"
      });
    }
  };

  const handleDeleteRelationship = async () => {
    if (!deleteRelId) return;
    try {
      await deleteItem('entities', 'relationships', deleteRelId);
      toast({ title: t('relationships.relationship_deleted') });
      setDeleteRelId(null);
    } catch {
      toast({
        title: t('relationships.relationship_delete_error_title'),
        description: t('relationships.relationship_delete_error_desc'),
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Typography color="error">{t('relationships.load_error')}</Typography>;
  }

  const visibleRelationships = relationships.filter((relationship) => (
    isOrganizationHierarchyEnabled
    || !isOrganizationHierarchyManagedRelationship(relationship.rawRelationship)
  ));
  const generalRelationships = visibleRelationships.filter(r => r.rawRelationship.forwardDescription !== 'is part of');
  const codexCategoryMap = novel.codexCategories || {};
  const codexCategories = new Set(Object.keys(codexCategoryMap));
  generalRelationships.forEach(({ target }) => {
    const codexCategory = getCodexCategory(target);
    if (codexCategory) codexCategories.add(codexCategory);
  });
  const filterOptions: RelationshipEntityTypeFilterOption[] = [
    { id: 'characters', label: t('entity_list.characters.title') },
    { id: 'locations', label: t('entity_list.locations.title') },
    { id: 'organizations', label: t('entity_list.organizations.title') },
    { id: 'events', label: t('entity_list.events.title') },
    ...Array.from(codexCategories).map((category) => ({
      id: `codex:${category}`,
      label: codexCategoryMap[category] || category,
    })),
  ];
  const filteredRelationships = generalRelationships.filter(({ target }) => {
    return !hiddenEntityTypeIds.has(getEntityTypeFilterId(target));
  });
  const relationshipPendingDelete = generalRelationships.find((relationship) => relationship.id === deleteRelId);
  const relationshipPendingDeleteLines = (() => {
    if (!relationshipPendingDelete) return [t('relationships.delete_item_name')];

    const { rawRelationship, target } = relationshipPendingDelete;
    const isEntityA = rawRelationship.entityAId === entity.id;
    const forwardDescription = rawRelationship.forwardDescription.trim();
    const reverseDescription = rawRelationship.reverseDescription?.trim();
    const outgoingDescription = isEntityA
      ? forwardDescription
      : (reverseDescription || t('relationships.related_to'));
    const incomingDescription = isEntityA ? reverseDescription : forwardDescription;
    const isSymmetric = Boolean(
      reverseDescription
      && forwardDescription.localeCompare(reverseDescription, undefined, { sensitivity: 'accent' }) === 0
    );
    const outgoingDetails = `${entity.name} ${outgoingDescription} ${target.name}`;

    if (!incomingDescription || isSymmetric) return [outgoingDetails];
    return [outgoingDetails, `${target.name} ${incomingDescription} ${entity.name}`];
  })();

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardHeader
        title={t('relationships.existing_title')}
        subheader={t('relationships.existing_subtitle', { entityName: entity.name })}
        titleTypographyProps={{ variant: 'h4', component: 'h2', fontWeight: 700 }}
        subheaderTypographyProps={{ variant: 'body1' }}
        action={(
          <RelationshipEntityTypeFilter
            hiddenEntityTypeIds={hiddenEntityTypeIds}
            onShowAll={showAllEntityTypes}
            onToggle={toggleEntityType}
            options={filterOptions}
          />
        )}
      />
      <CardContent>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'repeat(2, minmax(0, 1fr))',
            },
          }}
        >
          {filteredRelationships.length > 0 ? (
            filteredRelationships.map(rel => {
              return (
                <ExistingRelationshipCard
                  key={rel.id}
                  entity={entity}
                  target={rel.target}
                  relationship={rel.rawRelationship}
                  returnView={returnView}
                  isEditing={editingRelId === rel.id}
                  editingForwardDescription={editingForwardDesc}
                  editingReverseDescription={editingReverseDesc}
                  onEditingForwardDescriptionChange={setEditingForwardDesc}
                  onEditingReverseDescriptionChange={setEditingReverseDesc}
                  onStartEditing={() => {
                    setEditingRelId(rel.id);
                    setEditingForwardDesc(rel.rawRelationship.forwardDescription);
                    setEditingReverseDesc(rel.rawRelationship.reverseDescription || '');
                  }}
                  onCancelEditing={() => setEditingRelId(null)}
                  onSave={() => handleUpdateRelationship(rel.id, rel.rawRelationship)}
                  onDelete={() => setDeleteRelId(rel.id)}
                />
              );
            })
          ) : generalRelationships.length > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('relationships.no_relationships_match_filters')}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('relationships.no_relationships_for_entity')}
            </Typography>
          )}
        </Box>
      </CardContent>

      <DeleteConfirmationModal
        isOpen={!!deleteRelId}
        onClose={() => setDeleteRelId(null)}
        onConfirm={handleDeleteRelationship}
        itemNameLines={relationshipPendingDeleteLines}
        description={t('relationships.delete_description')}
      />
    </Card>
  );
}

interface AddRelationshipFormProps {
  sourceEntity: AnyEntity;
  onClose: () => void;
  onRelationshipAdded: () => void;
}

function AddRelationshipForm({
  sourceEntity,
  onClose,
  onRelationshipAdded,
}: Readonly<AddRelationshipFormProps>) {
  const [targetTypeValue, setTargetTypeValue] = useState<string | null>(null);
  const [targetEntity, setTargetEntity] = useState<AnyEntity | null>(null);
  const [forwardDescription, setForwardDescription] = useState('');
  const [isSymmetric, setIsSymmetric] = useState(false);
  const [reverseDescription, setReverseDescription] = useState('');
  const { t } = useTranslation();
  const { hasFeature } = useSubscription();
  const isOrganizationHierarchyEnabled = hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY);

  const {
    getNewId,
    novel,
    saveItem
  } = useNovelRepository();

  const targetTypeOptions = useMemo<TargetTypeOption[]>(() => {
    const categoryMap = novel.codexCategories;
    const categories = new Set(Object.keys(categoryMap));

    Object.values(novel.summaries.codex).forEach((entry) => {
      if ('type' in entry && entry.type) categories.add(entry.type);
    });

    const codexTargetTypes = Array.from(categories).map((category) => {
      const categoryLabel = categoryMap[category] || category;

      return {
        value: `codex:${category}`,
        entityType: 'codex' as const,
        label: categoryLabel,
        itemLabel: categoryLabel,
        codexCategory: category,
      };
    });

    const builtInOptions = builtInTargetTypes.map((option) => ({
      value: option.value,
      entityType: option.entityType,
      label: t(`relationships.target_types.${option.entityType}`),
      itemLabel: t(`relationships.target_type_items.${option.entityType}`),
    }));

    return [...builtInOptions, ...codexTargetTypes];
  }, [novel.codexCategories, novel.summaries.codex, t]);

  const selectedTargetType = targetTypeOptions.find((option) => option.value === targetTypeValue) || null;
  const targetStorageType = selectedTargetType?.entityType || 'characters';

  const { data: targetEntities, isLoading } = useEntities<AnyEntity>(targetStorageType);
  const { data: allRelationships } = useAllRelationships();

  const availableTargetEntities = useMemo(() => {
    return (targetEntities || []).filter((entity) => {
      if (selectedTargetType?.codexCategory && entity.type !== selectedTargetType.codexCategory) {
        return false;
      }

      return entity.id !== sourceEntity.id;
    });
  }, [selectedTargetType?.codexCategory, sourceEntity.id, targetEntities]);

  const relationshipSuggestions = useMemo(() => {
    const translatedSuggestions = genericRelationshipSuggestions.map((key) => t(key));
    if (!allRelationships) return translatedSuggestions;

    const existingDescriptions = allRelationships
      .filter((relationship) => (
        isOrganizationHierarchyEnabled
        || !isOrganizationHierarchyManagedRelationship(relationship)
      ))
      .flatMap(r => [r.forwardDescription, r.reverseDescription])
      .filter(Boolean)
      .map((description) => description!.trim())
      .filter(Boolean);
    const combined = [...new Set([...translatedSuggestions, ...existingDescriptions])];
    return combined;
  }, [allRelationships, isOrganizationHierarchyEnabled, t]);

  const { toast } = useToast();

  const handleAddRelationship = async () => {
    if (!targetEntity || !forwardDescription.trim() || !selectedTargetType) {
      toast({
        title: t('relationships.missing_info_title'),
        description: t('relationships.missing_info_desc'),
        variant: 'destructive',
      });
      return;
    }

    let finalReverseDescription = reverseDescription;
    if (isSymmetric) {
      finalReverseDescription = forwardDescription;
    }

    try {
      const newRelationship: Relationship = {
        id: getNewId(),
        entityAId: sourceEntity.id,
        entityAType: sourceEntity.entityType,
        entityBId: targetEntity?.id || '',
        entityBType: selectedTargetType.entityType,
        forwardDescription: forwardDescription,
      };
      const trimmedReverseDescription = finalReverseDescription.trim();
      if (trimmedReverseDescription) {
        newRelationship.reverseDescription = trimmedReverseDescription;
      }

      await saveItem('entities', 'relationships', newRelationship);

      toast({
        title: t('relationships.relationship_added'),
        description: t('relationships.relationship_added_desc', {
          sourceName: sourceEntity.name,
          targetName: targetEntity?.name,
        }),
      });

      // Reset form
      setTargetTypeValue(null);
      setTargetEntity(null);
      setForwardDescription('');
      setReverseDescription('');
      setIsSymmetric(false);
      onRelationshipAdded();
    } catch {
      toast({
        title: t('relationships.save_error_title'),
        description: t('relationships.save_error_desc'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardHeader
        title={t('relationships.add_title')}
        subheader={t('relationships.add_subtitle', { entityName: sourceEntity.name })}
        titleTypographyProps={{ variant: 'h6', component: 'h2', fontWeight: 600 }}
        action={(
          <IconButton aria-label={t('relationships.close_add_relationship')} onClick={onClose} size="small">
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      />
      <Divider />
      <CardContent>
        <Stack spacing={3}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('relationships.select_target_type_label')}</InputLabel>
            <Select
              value={targetTypeValue || ''}
              label={t('relationships.select_target_type_label')}
              onChange={(e) => {
                setTargetTypeValue(e.target.value);
                setTargetEntity(null);
              }}
            >
              {targetTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedTargetType && (
            <Autocomplete
              size="small"
              options={availableTargetEntities}
              getOptionLabel={(option) => option.name}
              loading={isLoading}
              value={targetEntity}
              onChange={(_, newValue) => setTargetEntity(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('relationships.select_target_label', { targetType: selectedTargetType.itemLabel })}
                />
              )}
            />
          )}

          {targetEntity && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  {sourceEntity.name} → {targetEntity.name}
                </Typography>
                <Autocomplete
                  freeSolo
                  options={relationshipSuggestions}
                  value={forwardDescription}
                  onInputChange={(_, newValue) => setForwardDescription(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('relationships.describe_relationship_placeholder')}
                      label={t('relationships.describe_relationship_label')}
                    />
                  )}
                />
              </Box>

              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <FormControlLabel
                  control={<Switch checked={isSymmetric} onChange={(e) => setIsSymmetric(e.target.checked)} />}
                  label={<Typography variant="body2">{t('relationships.symmetric_relationship_label')}</Typography>}
                />

                {!isSymmetric && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      {targetEntity.name} → {sourceEntity.name}
                    </Typography>
                    <Autocomplete
                      freeSolo
                      options={relationshipSuggestions}
                      value={reverseDescription}
                      onInputChange={(_, newValue) => setReverseDescription(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t('relationships.reverse_relationship_placeholder')}
                          label={t('relationships.reverse_relationship_label')}
                        />
                      )}
                    />
                  </Box>
                )}
              </Box>

              <Button
                variant="contained"
                onClick={handleAddRelationship}
                disabled={!targetEntity || !forwardDescription.trim()}
                startIcon={<Add sx={{ fontSize: '18px' }} />}
              >
                {t('relationships.save_relationship')}
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

interface AddRelationshipDialogProps {
  open: boolean;
  sourceEntity: AnyEntity;
  onClose: () => void;
}

function AddRelationshipDialog({
  open,
  sourceEntity,
  onClose,
}: Readonly<AddRelationshipDialogProps>) {
  const { t } = useTranslation();

  return (
    <Dialog
      aria-label={t('relationships.add_title')}
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ p: 0, '&:first-of-type': { pt: 0 } }}>
        <AddRelationshipForm
          sourceEntity={sourceEntity}
          onClose={onClose}
          onRelationshipAdded={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}

export const Route = createFileRoute('/relationships/$entityType/$id')({
  validateSearch: (search: Record<string, unknown>) => ({
    view: getRelationshipViewMode(search.view),
  }),
  component: EntityRelationshipPage,
});

function EntityRelationshipContent() {
  const { entityType, id } = Route.useParams();
  const { view } = Route.useSearch();
  const { t, i18n } = useTranslation();
  const {
    novel,
  } = useNovelRepository();
  const novelId = novel.id;

  const { data: entityData, isLoading } = useEntity<AnyEntity>(entityType as EntityType, id);
  const entity = entityData ? { ...entityData, entityType: entityType } : null;
  const [isAddRelationshipOpen, setIsAddRelationshipOpen] = useState(false);

  if (isLoading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  if (!entity || !novelId) {
    return (
      <Page title={t('relationships.entity_not_found_title')} layoutMode="scrolling">
        <Typography>{t('relationships.entity_not_found_desc')}</Typography>
      </Page>
    );
  }

  const isLocation = entityType === 'locations';
  const getRelationshipEntityLabel = (
    group: 'target_type_items' | 'target_types',
    fallback: string,
  ) => {
    const key = `relationships.${group}.${entityType}`;
    return i18n.exists(key) ? t(key) : fallback;
  };
  const entityKindLabel = getRelationshipEntityLabel(
    'target_type_items',
    capitalizeFirstLetter(entityType.slice(0, -1)),
  );
  const entityTypeLabel = getRelationshipEntityLabel(
    'target_types',
    capitalizeFirstLetter(entityType),
  );
  return (
    <Page
      title={t('relationships.detail_title', { entityName: entity.name })}
      description={t('relationships.detail_description', { entityKind: entityKindLabel })}
        breadcrumbs={[
          { label: t('relationships.title'), href: `/relationships?view=${view}` },
          { label: entityTypeLabel, href: `/${entityType}` },
          { label: entity.name, href: `/${entityType}/view/${entity.id}` },
          { label: t('relationships.breadcrumb_current') }
        ]}
        actions={
          <IconButton
            aria-label={t('relationships.back_to_relationship_map')}
            component={Link}
            disableRipple
            size="medium"
            sx={{
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'transparent',
                color: 'primary.dark',
              },
            }}
            to={`/relationships?view=${view}`}
          >
            <Close sx={{ fontSize: 26 }} />
          </IconButton>
        }
      >
      <Box sx={{ mt: 4 }}>
        <RelationshipEntityHero
          entity={entity}
          entityKindLabel={entityKindLabel}
          onAddRelationship={() => setIsAddRelationshipOpen(true)}
        />
      </Box>
      {isLocation && (
        <Box sx={{ mt: 4 }}>
          <PartOfRelationshipManager location={entity as Location} />
        </Box>
      )}
      <Box sx={{ mt: 4 }}>
        <ExistingRelationships entity={entity} novelId={novelId} returnView={view} />
      </Box>
      <AddRelationshipDialog
        open={isAddRelationshipOpen}
        sourceEntity={entity}
        onClose={() => setIsAddRelationshipOpen(false)}
      />
    </Page>
  );
}

export default function EntityRelationshipPage() {
  return (
    <AuthGuard>
      <Suspense fallback={<Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}>
        <EntityRelationshipContent />
      </Suspense>
    </AuthGuard>
  );
}
